import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Context } from '@front10/helpers/dist/Context';
import { modifiers, isPastDate, isSameDate } from '../../../helpers/dayCard/dayCard';

import Box from '../../elements/Box';
import Text from '../../elements/Text';
import FareItemReturnInfo from '../_commons/atoms/FareItemReturnInfo';
import FareItemRatePrice from '../_commons/atoms/FareItemRatePrice';
import FareItemTripDuration from '../_commons/atoms/FareItemTripDuration';
import FareItemNoPriceAvailable from '../_commons/atoms/FareItemNoPriceAvailable';
import FareItemDate from '../_commons/atoms/FareItemDate';
import Separator from '../_commons/atoms/Separator';
import ErrorBoundary from '../../utils/ErrorBoundary';
import { getDateAriaLabel } from '../_commons/atomsHelpers';
import { handleFareItemInteraction } from '../_commons/commonFns';

const KEY_ENTER = 13;
const KEY_SPACE = 32;

const RateCard = (props) => {
  const {
    data,
    isLowest,
    day,
    usedErrorBoundary,
    tabIndex,
    tabIndexCallToAction,
    showDate,
    showSeparator,
    focusReferences,
    isToday,
    ariaLabel,
    ariaLabelFormat,
    isDisregarded,
    isOutside,
    dayDateFormat,
    availableAtomsBody = [
      'FareItemRatePrice',
      'FareItemReturnInfo',
      'FareItemIsLowest',
      'FareItemTripDuration'
    ],
    isDesktop,
    focusDate,
    accessibleByArrowKeys = true,
    isFirstDay,
    firstDateFocusable,
    userInteraction,
    setUserInteraction
  } = props;

  const dayCard = modifiers(props);
  const { translate, localizationData } = useContext(Context);
  const dayCardRef = React.useRef(null);
  let srMessage = '';

  const {
    handleCallToAction,
    handleClick,
    handleCallToActionKeyDown,
    hasInteraction
  } = handleFareItemInteraction(props);

  React.useEffect(() => {
    if (accessibleByArrowKeys && userInteraction && focusDate) {
      if (dayjs(focusDate).isSame(day, 'day')) {
        dayCardRef?.current.focus();
        setUserInteraction(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDate, dayCardRef]);

  const cursorClass = hasInteraction ? 'fr-cursor-pointer' : '';

  if (ariaLabel) {
    srMessage += `${ariaLabel}`;
  }
  if (isLowest) {
    srMessage += `${srMessage ? ',' : ''} ${translate(
      'cmp-legend-lowest-price',
      'Lowest price'
    )}`.trim();
  }
  if (isFirstDay || isSameDate(day, firstDateFocusable)) {
    if (ariaLabel === null) {
      srMessage += `${srMessage ? '. ' : ''} ${translate(
        'cmp-low-calendar-container-explain',
        'This is a calendar with the cheapest fares per day.'
      )}`.trim();
    }
    srMessage += `${srMessage ? '. ' : ''} ${translate(
      'cmp-legend-instructions',
      'To navigate the days on the calendar use arrow keys once the day is focus and press the enter key to select it.'
    )}`.trim();
  }
  // if (isToday) {
  //   srMessage += `${srMessage ? ',' : ''} ${translate(
  //     'cmp-legend-current-day',
  //     'Current day'
  //   )}`.trim();
  // }
  srMessage = srMessage || null;

  let ariaLabelDay = null;
  if (data && data.departureDate) {
    ariaLabelDay = getDateAriaLabel(
      data.departureDate,
      translate,
      ariaLabelFormat,
      localizationData
    );
  } else if (day) {
    ariaLabelDay = getDateAriaLabel(day, translate, ariaLabelFormat, localizationData);
  }
  let dataTestId = 'dayCard';
  if (isOutside) {
    dataTestId = 'dayCard--outside';
  }
  if (isDisregarded) {
    dataTestId = 'dayCard--disregarded';
  }
  if (isPastDate(day)) {
    dataTestId = 'dayCard--pastdate';
  }
  const isInteractive =
    !(isPastDate(day) || isDisregarded || !tabIndex || isOutside) &&
    data &&
    data.dataType &&
    data.dataType !== 'EMPTY';

  let elements = {
    FareItemReturnInfo: (
      <FareItemReturnInfo key="drinfo" {...dayCard.dayCardReturnInfo} {...data} />
    ),
    FareItemTripDuration: (
      <FareItemTripDuration key="duration" {...dayCard.dayCardTripDuration} {...data} />
    ),
    FareItemIsLowest: isLowest && (
      <Box
        key="islowest"
        className="fare-item-is-lowest"
        style={{
          background: '#00855C',
          height: '9px',
          width: '9px',
          '-moz-border-radius': '50%',
          '-webkit-border-radius': '50%',
          'border-radius': '50%'
        }}
        {...dayCard.dayCardIsLowest}
      ></Box>
    ),
    FareItemRatePrice: <FareItemRatePrice key="price" {...dayCard.dayCardRatePrice} {...data} />
  };
  let dayCardProps = { ...dayCard };
  if (isToday) {
    dayCardProps['ariaLabel'] = null;
  }

  React.useLayoutEffect(() => {
    if (isDesktop) {
      dayCardRef?.current?.parentNode?.setAttribute('role', 'presentation');
      dayCardRef?.current?.parentNode?.parentNode?.setAttribute('role', 'presentation');
      dayCardRef?.current?.parentNode?.parentNode?.removeAttribute?.('tabindex');
      dayCardRef?.current?.parentNode?.parentNode?.removeAttribute?.('aria-label');
      dayCardRef?.current?.parentNode?.parentNode?.removeAttribute?.('aria-disabled');
      dayCardRef?.current?.parentNode?.parentNode?.removeAttribute?.('aria-selected');
    }
  }, [dayCardRef, isDesktop]);

  const cardRender = (
    <Box
      display="flex"
      flexDirection="column"
      role="gridcell"
      dataTestId={dataTestId}
      className={`${isDesktop ? 'fr-day-card' : 'fr-dar-rate-card'} ${cursorClass}`}
      {...dayCardProps}
      focusReferences={accessibleByArrowKeys ? dayCardRef : focusReferences}
      onKeyDown={(e) => {
        if (isInteractive) {
          if (e.keyCode === KEY_ENTER || e.keyCode === KEY_SPACE) {
            handleClick();
          } else if (props.setFocusDate) {
            props.setFocusDate(day, e);
          }
        }
      }}
      onFocus={(e) => {
        if (props.onFocus) {
          props.onFocus(e, day);
        }
      }}
      onClick={isInteractive && handleClick}
      tabIndex={
        isInteractive
          ? focusDate === null
            ? isFirstDay
              ? 0
              : -1
            : dayjs(focusDate).isSame(day, 'day')
            ? 0
            : -1
          : null
      }
      focusable={tabIndex}
      style={dayCard.style}
      ariaCurrent={isToday ? 'date' : undefined}
    >
      {srMessage && <Text className="fr-sr-only">{srMessage}</Text>}
      {!showDate && !dayCard.showDay && ariaLabelDay && (
        <Text className="fr-sr-only" ariaHidden={!isInteractive}>
          {ariaLabelDay}
        </Text>
      )}
      {dayCard.showDay && ariaLabelDay && (
        <>
          <Text
            position="absolute"
            right="1"
            top="1"
            {...dayCard.dayCardDay}
            ariaLabel={ariaLabelDay}
          >
            {dayjs(day).format(dayDateFormat)}
          </Text>
        </>
      )}
      {!isPastDate(day) && (
        <>
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
            flex="1"
            {...dayCard.dayCardBody}
          >
            {data && data.dataType && data.dataType === 'DATA_API' && (
              <>{availableAtomsBody.map((e) => elements[e])}</>
            )}
            {data && data.dataType && data.dataType === 'EMPTY' && (
              <>
                {showDate && (
                  <FareItemDate
                    {...dayCard.dayCardDate}
                    date={data && data.departureDate && data.departureDate}
                  />
                )}
                <Box
                  className={`fare-item-empty-trip-duration`}
                  {...dayCard.dayCardEmptyTripDuration}
                ></Box>
              </>
            )}

            {data && data.dataType && data.dataType === 'EMPTY_FROM_API' && (
              <>
                {showDate && (
                  <FareItemDate
                    {...dayCard.dayCardDate}
                    date={data && data.departureDate && data.departureDate}
                  />
                )}
                {showSeparator && <Separator content="" {...dayCard.dayCardSeparator} />}
                <FareItemNoPriceAvailable
                  {...dayCard.dayCardNoPriceAvailable}
                  tabIndex={tabIndexCallToAction}
                  onClick={handleCallToAction}
                  onKeyDown={handleCallToActionKeyDown}
                />
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  return usedErrorBoundary ? <ErrorBoundary>{cardRender}</ErrorBoundary> : { cardRender };
};

RateCard.propTypes = {
  /**
   * Raw data passed to the object
   */
  data: PropTypes.objectOf(PropTypes.any),
  /**
   * The day to render
   */
  day: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the day has the lowest price
   */
  isLowest: PropTypes.bool,
  /**
   * Determine if the day is today
   */
  isToday: PropTypes.bool,
  /**
   * Determine if the day is outside.
   */
  isOutside: PropTypes.bool,
  /**
   * Props of dayCardBody
   */
  dayCardBody: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardFooter
   */
  dayCardFooter: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardReturnInfo
   */
  dayCardReturnInfo: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardTravelClass
   */
  dayCardTravelClass: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardJourneyType
   */
  dayCardJourneyType: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardJourneyTypeTravelClass
   */
  dayCardJourneyTypeTravelClass: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardLastSeen
   */
  dayCardLastSeen: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardPriceAvg
   */
  dayCardPriceAvg: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardMiles
   */
  dayCardMiles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardDay
   */
  dayCardDay: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardNoPriceAvailable
   */
  dayCardNoPriceAvailable: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardNoPriceAvailable
   */
  dayCardDate: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardNoPriceAvailableCallToAction
   */
  dayCardNoPriceAvailableCallToAction: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardCallToAction
   */
  dayCardCallToAction: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardSeparatorJourneyType
   */
  dayCardSeparatorJourneyType: PropTypes.objectOf(PropTypes.any),
  /**
   * Format applied to the dayCardDay
   */
  dayDateFormat: PropTypes.string,
  /**
   * Turn off or on the showReturnInfo section
   */
  showReturnInfo: PropTypes.bool,
  /**
   * Turn off or on the showTravelClass section
   */
  showTravelClass: PropTypes.bool,
  /**
   * Turn off or on the showJourneyType section
   */
  showJourneyType: PropTypes.bool,
  /**
   * Turn off or on the showLastSeen section
   */
  showLastSeen: PropTypes.bool,
  /**
   * Turn off or on the showPrice section
   */
  showPrice: PropTypes.bool,
  /**
   * Turn off or on the showSeparator section
   */
  showSeparator: PropTypes.bool,
  /**
   * Turn off or on the showMiles section
   */
  showMiles: PropTypes.bool,
  /**
   * Turn off or on the showDay section
   */
  showDay: PropTypes.bool,
  /**
   * Turn off or on the showNoPriceAvailableCallToAction section
   */
  showNoPriceAvailableCallToAction: PropTypes.bool,
  /**
   * Turn off or on the showCallToAction section
   */
  showCallToAction: PropTypes.bool,
  /**
   * Define if conponent is loading
   */
  loading: PropTypes.bool,
  /**
   * Props of currentDayStyles
   */
  currentDayStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of pastDayStyles
   */
  pastDayStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of outsideDayStyles
   */
  outsideDayStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of lowestPriceStyles
   */
  lowestPriceStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of noPriceAvailableStyles
   */
  noPriceAvailableStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of currentDayAndLowestPriceStyles
   */
  currentDayAndLowestPriceStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  interactionHotZone: PropTypes.oneOf(['smart', 'whole', 'button']),
  /**
   * OnClick event
   */
  onClick: PropTypes.func,
  /**
   * Call to action onClick event
   */
  callToActionOnClick: PropTypes.func,
  /**
   * Determine if the component is protected using ErrorBoundary.
   * By default all components protect themselves
   */
  usedErrorBoundary: PropTypes.bool,
  /**
   * Lowest Price key message for Screen Readers
   */
  lowestPriceMessage: PropTypes.string,
  /**
   * Define if the dayCard will have focus for Screen Readers
   */
  tabIndex: PropTypes.bool,
  /**
   * Function to handle focus renference of the component
   */
  focusReferences: PropTypes.func,
  /**
   * Define if the CallToAction will have focus for Screen Readers
   */
  tabIndexCallToAction: PropTypes.bool,
  /**
   * Define if the CallToAction will have focus for Screen Readers
   */
  showDate: PropTypes.bool,
  /**
   * Cursor of the dayCard
   */
  cursor: PropTypes.oneOf(['default', 'pointer']),
  ariaLabel: PropTypes.string,
  ariaLabelFormat: PropTypes.string,
  disregardedBehavior: PropTypes.oneOf(['blank-date', 'past-date', 'glass-date']),
  isDisregarded: PropTypes.bool
};

RateCard.defaultProps = {
  showSeparator: false,
  data: null,
  showDate: false,
  day: null,
  isLowest: false,
  isToday: false,
  isOutside: false,
  dayCardDate: {},
  dayCardBody: {},
  dayCardFooter: {},
  dayCardReturnInfo: {},
  dayCardJourneyTypeTravelClass: {},
  dayCardTravelClass: {},
  dayCardJourneyType: {},
  dayCardLastSeen: {},
  dayCardPriceAvg: {},
  dayCardMiles: {},
  dayCardDay: {},
  dayCardNoPriceAvailable: {},
  dayCardNoPriceAvailableCallToAction: {},
  dayCardCallToAction: {},
  dayCardSeparatorJourneyType: {},
  dayDateFormat: 'D',
  showReturnInfo: true,
  showTravelClass: true,
  showJourneyType: false,
  showLastSeen: true,
  showPrice: true,
  showMiles: false,
  showDay: true,
  showNoPriceAvailableCallToAction: true,
  showCallToAction: false,
  loading: false,
  usedErrorBoundary: true,
  cursor: 'pointer',
  currentDayStyles: {
    borderColor: 'primary'
  },
  pastDayStyles: {
    disabled: true,
    bgColor: 'light',
    textColor: 'dark-6',
    cursor: 'default',
    dayCardDay: {
      textColor: 'dark-9'
    }
  },
  outsideDayStyles: {
    showDay: false,
    bgColor: 'light',
    cursor: 'default'
  },
  lowestPriceStyles: {
    dayCardDay: {
      textColor: 'lowestFareText'
    },
    dayCardBody: {
      bgColor: 'lowestFareBg',
      textColor: 'lowestFareText'
    }
  },
  noPriceAvailableStyles: {},
  currentDayAndLowestPriceStyles: {},
  lowestPriceMessage: 'cmp-day-card-lowest-price',
  tabIndex: true,
  focusReferences: () => {},
  tabIndexCallToAction: false,
  onClick: () => {},
  callToActionOnClick: () => {},
  interactionHotZone: 'smart',
  ariaLabel: null,
  ariaLabelFormat: 'MMMM D',
  disregardedBehavior: 'past-date',
  isDisregarded: false,
  isDesktop: true
};

RateCard.componentName = 'RateCard';

export default RateCard;
